import { makeAutoObservable } from "mobx";

class PDFStore {
  isLoading = false;
  loadingIds: Set<string> = new Set();
  cache: Record<string, string> = {};

  constructor() {
    makeAutoObservable(this);
  }

  startLoading(id: string) {
    this.loadingIds.add(id);
    this.isLoading = true;
  }

  stopLoading(id: string) {
    this.loadingIds.delete(id);
    this.isLoading = this.loadingIds.size > 0;
  }

  isPdfLoading(id: string) {
    return this.loadingIds.has(id);
  }

  setCache(id: string, link: string) {
    this.cache[id] = link;
  }

  getFromCache(id: string): string | undefined {
    return this.cache[id];
  }

  async fetchPdf(id: string, apiCall: (id: string) => Promise<string | null>) {
    // ✅ Prevent duplicate fetches for the same ID
    if (this.isPdfLoading(id)) {
      console.log(`[PDFStore] Skipping duplicate fetch for ${id}`);
      return this.cache[id];
    }

    const cached = this.getFromCache(id);
    if (cached) {
      console.log(`[PDFStore] Using cached link for ${id}`);
      return cached;
    }

    this.startLoading(id);
    try {
      const link = await apiCall(id);
      if (link) {
        this.setCache(id, link);
        return link;
      } else {
        console.warn(`[PDFStore] API returned no link for ${id}, retrying...`);
        const retryLink = await apiCall(id);
        if (retryLink) {
          this.setCache(id, retryLink);
          return retryLink;
        }
      }
    } catch (e) {
      console.error(`[PDFStore] PDF fetch failed for ${id}:`, e);
    } finally {
      this.stopLoading(id);
    }
  }
}

export default PDFStore;
